/**
 * Club branding: logo file and colours.
 *
 * Kept apart from `teams.js` on purpose. That module is public fact
 * (names, locations, abbreviations) and stays that way. This one is club
 * intellectual property that we reproduce to identify a club, which is a
 * different kind of thing with a different licensing position. See
 * `scripts/fetch-team-logos.mjs`, which generates both this data and the
 * images, and `docs/known-limitations.md`.
 *
 * Nothing here is required for the product to work: an unknown club, or a
 * missing image, degrades to a lettered crest in the club's colours.
 */

import BRANDING from "./teamBranding.json";

/** Neutral grey, used for a club we have no branding for. */
const UNBRANDED = Object.freeze({
  name: null,
  primary: "#4b5563",
  secondary: "#d3d6db",
  onPrimary: "#ffffff",
});

/** Every club we hold branding for. */
export const BRANDED_TEAM_IDS = Object.freeze(Object.keys(BRANDING.teams));

/** Widths generated under public/logos/nfl/<width> for responsive logos. */
export const TEAM_LOGO_VARIANT_WIDTHS = Object.freeze([48, 72, 96, 128]);

/** Always returns something usable, so callers never branch on null. */
export function teamBranding(teamId) {
  return BRANDING.teams[teamId] ?? UNBRANDED;
}

export function hasTeamBranding(teamId) {
  return Boolean(BRANDING.teams[teamId]);
}

/**
 * Path to a club's logo, or null when we don't have one.
 *
 * Null rather than a guessed path: a src that 404s costs a request and a
 * broken-image flash before the fallback can take over.
 */
export function teamLogoSrc(teamId) {
  if (!hasTeamBranding(teamId)) return null;
  return `${process.env.PUBLIC_URL ?? ""}/logos/nfl/${teamId}.png`;
}

export function teamLogoVariantSrc(teamId, width, format = "png") {
  if (!hasTeamBranding(teamId)) return null;
  return `${process.env.PUBLIC_URL ?? ""}/logos/nfl/${width}/${teamId}.${format}`;
}

export function teamLogoSrcSet(teamId, format = "png") {
  if (!hasTeamBranding(teamId)) return null;

  return TEAM_LOGO_VARIANT_WIDTHS.map(
    (width) => `${teamLogoVariantSrc(teamId, width, format)} ${width}w`
  ).join(", ");
}

/**
 * A club colour that is safe to put text on, or to use as a bold fill.
 *
 * A few clubs' primary is white or near-white, which disappears against
 * our surfaces; for those the secondary is the one that reads.
 */
export function teamAccent(teamId) {
  const { primary, secondary, onPrimary } = teamBranding(teamId);
  const pale = onPrimary !== "#ffffff";
  return pale && secondary !== "#ffffff" ? secondary : primary;
}

/* ------------------------------------------------------------------ */
/* colour helpers for the club-tinted washes                           */
/* ------------------------------------------------------------------ */

const hexToRgb = (hex) => {
  const value = String(hex ?? "").replace("#", "");
  if (value.length !== 6) return null;
  const int = Number.parseInt(value, 16);
  if (Number.isNaN(int)) return null;
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
};

const relativeLuminance = ([r, g, b]) => {
  const channel = (c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/** How far from grey a colour is. Black and white both score 0. */
const chroma = ([r, g, b]) => Math.max(r, g, b) - Math.min(r, g, b);

/**
 * The club colour to tint a surface with.
 *
 * Usually the primary: most clubs' is a deep, saturated navy, green or
 * red that reads beautifully at low opacity. The exceptions are the clubs
 * whose primary is essentially black (Pittsburgh, Las Vegas, Houston):
 * tinting with black produces grey, which says nothing about the club, so
 * those fall through to the secondary. That is why a Steelers card washes
 * gold rather than charcoal.
 */
export function teamWashRgb(teamId) {
  const { primary, secondary } = teamBranding(teamId);

  const primaryRgb = hexToRgb(primary);
  if (!primaryRgb) return null;

  const isAchromaticBlack =
    chroma(primaryRgb) < 30 && relativeLuminance(primaryRgb) < 0.05;

  const chosen = isAchromaticBlack ? hexToRgb(secondary) ?? primaryRgb : primaryRgb;
  return chosen.join(", ");
}
